/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react'
import cn from 'classnames'
import { map, some, get, flatten, isNumber } from 'lodash'

import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import quantityValidator from '../../utils/quantityValidator'
import quantityUpdater from '../../utils/quantityUpdater'
import { NOTES_CHARACTER_LIMIT } from '../../constants'

import styles from './VNItemDetail.module.scss'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import ModifierGroup from '../../components/ModifierGroup'
import { Button, ButtonBase } from '@material-ui/core'
import SidebarModal from '../../components/SidebarModal'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { getItemForCurrentMenuById } from '../../selectors/items'
import { addToCart } from '../../actions/cart'
import { getIsKeyboardActive } from '../../VNAndroidSDK/Selectors'

const textFieldStyles = makeStyles({
  root: {
    // focused color for input with variant='outlined'
    '& .MuiOutlinedInput-root': {
      '&.MuiInputBase-formControl fieldset': {
        border: 0,
      },
      '&.Mui-focused fieldset': {
        border: 0,
      },
    },
  },
})

/**
 * VNItemDetail - used to add Modifier items to cart
 */
const VNItemDetail = ({ quantity = 1, notesAreEmpty = true, selectedItemIds = [], className, ...props }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { uuid } = useParams()

  const item = useSelector((state) => getItemForCurrentMenuById(state, uuid))
  const isKeyboardActive = useSelector(getIsKeyboardActive)

  const modifierGroups = get(item, 'modifierGroups', [])

  const id = item.id
  const title = item.name
  const isAlcohol = item.isAlcohol
  const price = `$${(item.defaultPriceInCents / 100).toFixed(2)}`
  const canEnterNotes = item.canEnterNotes
  const orderMaxAmount = item.orderMaxAmount ?? 999
  const orderMinCount = item.orderMinCount ?? 1

  const classNames = cn(styles.cartItemDetailsModal, className)
  const classes = textFieldStyles()
  const isMobile = useGetIsMobileScreen()
  const defaultModifiers = useMemo(() => {
    const output = []
    modifierGroups.forEach((modifierGroup) => {
      const curGroup = []
      modifierGroup.defaults?.forEach((modId) => {
        curGroup.push(`${modId}|${modifierGroup.uuid}`)
      })
      output.push(curGroup)
    })
    return output
  }, [modifierGroups])
  const [modifierGroupsSelections, setModifierGroupsSelections] = useState(defaultModifiers)
  const [validators, setValidators] = useState(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  const [isValid, setIsValid] = useState(false)
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const [isQuantityUpdating, setIsQuantityUpdating] = useState(false)
  const [notes, setNotes] = useState(null)
  const [inputValue, setInputValue] = useState(quantity)
  const [inputPlaceholder, setInputPlaceholder] = useState(quantity)
  const clientHeight = document?.body?.clientHeight
  const isMaxQuantity = inputValue === orderMaxAmount
  const isMinQuantity = inputValue === orderMinCount

  const onSelect = (itemId, index) => {
    const modifierGroup = modifierGroups[index]
    const { min, max } = modifierGroup
    const selectedModifiers = modifierGroupsSelections?.[index]

    const nextSelectedModifiers = [...modifierGroupsSelections]
    nextSelectedModifiers[index] = quantityUpdater(min, max, selectedModifiers, itemId)

    setModifierGroupsSelections(nextSelectedModifiers)
  }

  useEffect(() => {
    setInputPlaceholder(quantity)
  }, [quantity])

  useEffect(() => {
    const validations = map(validators, (validator, index) => {
      const modifierGroupSelections = modifierGroupsSelections?.[index]

      return validator.isValid(get(modifierGroupSelections, 'length', 0))
    })

    const isValid = !some(validations, (valid) => valid === false)
    setIsValid(isValid)
  }, modifierGroupsSelections)

  useEffect(() => {
    setValidators(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  }, [modifierGroups])

  let notesElement = null

  const hasCharacterMax = isNumber(NOTES_CHARACTER_LIMIT)

  const handleSetNotes = (event) => {
    const notes = event.target.value || ''

    if (hasCharacterMax) {
      setNotes(notes.substring(0, NOTES_CHARACTER_LIMIT))
      return
    }

    setNotes(notes)
  }

  const onFocus = () => {
    if (!isQuantityUpdating) {
      setIsKeyboardOpen(true)
    }
  }
  const onBlur = () => {
    setIsKeyboardOpen(false)
  }
  // needs to be done at add to cart action
  if (canEnterNotes) {
    notesElement = (
      <div className={cn(styles.notesContainer, { [styles.emptyNotes]: notesAreEmpty })}>
        <TextField
          id="special-instructions"
          label="Add Special Instructions"
          value={notes}
          onChange={handleSetNotes}
          className={cn(styles.notes, classes.root)}
          InputLabelProps={{ className: styles.textfield__label }}
          inputProps={{ 'data-allow-keyboard-activity': true }}
          InputProps={{ className: styles.textfield__input }}
          variant="outlined"
          multiline
          onFocus={onFocus}
          onBlur={onBlur}
          rows={5}
        />
      </div>
    )
  }

  const incrementQuantity = () => {
    const newValue = inputValue === orderMaxAmount ? orderMaxAmount : inputValue + 1
    setInputValue(newValue)
  }

  const decrementQuantity = () => {
    const newValue = inputValue === orderMinCount ? orderMinCount : inputValue - 1
    setInputValue(newValue)
  }

  const handleInput = (e) => {
    const input = get(e, 'target.value', '')
    setInputValue(input)
  }

  const handleSetQuantity = (inputValue) => {
    let newValue = inputValue
    if (!inputValue || inputValue < orderMinCount) {
      newValue = orderMinCount
    }
    if (inputValue > orderMaxAmount) {
      newValue = orderMaxAmount
    }
    setInputValue(newValue)
  }

  const handleSubmit = (e) => {
    setIsValid(false)
    dispatch(addToCart({ itemId: id, modifiers: flatten(modifierGroupsSelections), quantity: inputValue, isAlcohol, notes }))

    history.goBack()
  }

  return (
    <SidebarModal show title="Item Detail" onClose={() => history.goBack()} className={cn(classNames, styles.header)} {...props}>
      <div className={styles.cartItemDetailsModal}>
        <div className={styles.scrollable} style={isKeyboardOpen ? { height: '80vh' } : null}>
          <div className={styles.header}>
            <div className={styles.itemName}>{title}</div>
            <div className={styles.price}>{price}</div>
          </div>
          {modifierGroups.map((modifierGroup, index) => {
            if (!modifierGroup.name) {
              return null
            }
            return (
              <ModifierGroup
                key={index}
                className={styles.modifierGroup}
                title={modifierGroup.name}
                label={get(validators, `[${index}].label`)}
                items={modifierGroup.items}
                selectedItemIds={modifierGroupsSelections?.[index]}
                isRequired={get(validators, `[${index}].validationType`) === quantityValidator.types.required}
                min={modifierGroup.min}
                max={modifierGroup.max}
                onSelect={(itemId) => onSelect(itemId, index)}
                uuid={modifierGroup.uuid}
              />
            )
          })}
          {notesElement}
        </div>

        {/* isKeyboardActive Remove this isMobile nonesense use css instead */}
        {(!isKeyboardActive || isQuantityUpdating) && (
          <div className={styles.footer} style={isMobile && isQuantityUpdating && clientHeight > 800 ? { top: '48vh' } : null}>
            <div className={styles.updateQuantity}>
              <ButtonBase
                component="span"
                className={isMinQuantity ? cn(styles.disabled, styles.minus) : cn(styles.quantityOperator, styles.minus)}
                onClick={decrementQuantity}
              >
                –
              </ButtonBase>
              <input
                type="number"
                className={styles.quantity}
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={handleInput}
                onFocus={() => {
                  setIsQuantityUpdating(true)
                  setInputPlaceholder('')
                }}
                onBlur={() => {
                  setIsQuantityUpdating(false)
                  handleSetQuantity(+inputValue)
                }}
                pattern="\d*"
              />
              <ButtonBase
                component="span"
                className={isMaxQuantity ? cn(styles.disabled, styles.plus) : cn(styles.quantityOperator, styles.plus)}
                onClick={incrementQuantity}
              >
                +
              </ButtonBase>
            </div>
            <div className={styles.container}>
              <Button className={styles.submit} disabled={!isValid} onClickCapture={handleSubmit}>
                <span>Add to Cart</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </SidebarModal>
  )
}

export default VNItemDetail
